import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { API_URL } from '../config';
import PageHeader from './PageHeader';

const BackupRestore = ({ companies, users, userId, onRestore, isAdmin = false, isDarkMode = false }) => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [exportFormat, setExportFormat] = useState('json');
  const [exportScope, setExportScope] = useState(isAdmin ? 'all' : 'current'); // Admin má výchozí "all"

  const createBackup = async (format = exportFormat, scope = exportScope) => {
    setIsExporting(true);
    console.log('=== BACKUP DEBUG ===');
    console.log('Format:', format);
    console.log('Scope:', scope);
    console.log('IsAdmin:', isAdmin);
    console.log('ExportScope state:', exportScope);
    console.log('Condition (scope === all && isAdmin):', scope === 'all' && isAdmin);
    
    try {
      let companiesData, usersData, newsData, messagesData, userKnowledgeData, knowledgeData;
      let aiData, batchVisibility, requestTemplates, companyStatuses, roles, companyTransfers, fieldMappings;
      
      if (scope === 'all' && isAdmin) {
        console.log('🔥 ADMIN FULL BACKUP - fetching all data');
        
        // Test connectivity před zahájením backup
        try {
          console.log('🧪 Testing connectivity to:', `${API_URL}/companies?page=1&limit=1`);
          const testRes = await fetch(`${API_URL}/companies?page=1&limit=1`);
          if (!testRes.ok) {
            throw new Error(`Connectivity test failed: ${testRes.status} ${testRes.statusText}`);
          }
          console.log('✅ Connectivity test passed');
        } catch (testError) {
          console.error('❌ Connectivity test failed:', testError);
          throw new Error(`Nelze se připojit k API serveru: ${testError.message}`);
        }
        
        // Export všech dat pro admin
        try {
          const companiesRes = await fetch(`${API_URL}/companies?all=true&userId=${userId || ''}`);
          if (!companiesRes.ok) {
            throw new Error(`Companies fetch failed: ${companiesRes.status} ${companiesRes.statusText}`);
          }
          const companiesResponse = await companiesRes.json();
          // Backend může vracet buď přímo array nebo objekt s companies property
          companiesData = companiesResponse.companies || companiesResponse;
          console.log('Companies fetched:', companiesData.length);
          
          const usersRes = await fetch(`${API_URL}/users?userId=${userId || ''}`);
          if (!usersRes.ok) {
            throw new Error(`Users fetch failed: ${usersRes.status} ${usersRes.statusText}`);
          }
          usersData = await usersRes.json();
          console.log('Users fetched:', usersData.length);

          // Export novinek a zpráv
          const newsRes = await fetch(`${API_URL}/news`);
          if (!newsRes.ok) {
            throw new Error(`News fetch failed: ${newsRes.status} ${newsRes.statusText}`);
          }
          newsData = await newsRes.json();
          console.log('News fetched:', newsData.length);

          const messagesRes = await fetch(`${API_URL}/messages/group`);
          if (!messagesRes.ok) {
            throw new Error(`Messages fetch failed: ${messagesRes.status} ${messagesRes.statusText}`);
          }
          messagesData = await messagesRes.json();
          console.log('Messages fetched:', messagesData.length);

          // Export znalostí
          const userKnowledgeRes = await fetch(`${API_URL}/user-knowledge`);
          if (!userKnowledgeRes.ok) {
            throw new Error(`User knowledge fetch failed: ${userKnowledgeRes.status} ${userKnowledgeRes.statusText}`);
          }
          userKnowledgeData = await userKnowledgeRes.json();
          console.log('User knowledge fetched:', userKnowledgeData.length);

          const knowledgeRes = await fetch(`${API_URL}/knowledge`);
          if (!knowledgeRes.ok) {
            throw new Error(`Knowledge fetch failed: ${knowledgeRes.status} ${knowledgeRes.statusText}`);
          }
          knowledgeData = await knowledgeRes.json();
          console.log('Knowledge structure fetched:', knowledgeData.length);

          // Export AI dat
          try {
            const aiDataRes = await fetch(`${API_URL}/ai-data`);
            aiData = aiDataRes.ok ? await aiDataRes.json() : [];
            console.log('AI data fetched:', aiData.length);
          } catch (error) {
            console.warn('AI data not available:', error);
            aiData = [];
          }

          // Export batch visibility
          try {
            const batchVisibilityRes = await fetch(`${API_URL}/batch-visibility`);
            batchVisibility = batchVisibilityRes.ok ? await batchVisibilityRes.json() : [];
            console.log('Batch visibility fetched:', batchVisibility.length);
          } catch (error) {
            console.warn('Batch visibility not available:', error);
            batchVisibility = [];
          }

          // Export request templates
          try {
            const requestTemplatesRes = await fetch(`${API_URL}/request-templates`);
            requestTemplates = requestTemplatesRes.ok ? await requestTemplatesRes.json() : [];
            console.log('Request templates fetched:', requestTemplates.length);
          } catch (error) {
            console.warn('Request templates not available:', error);
            requestTemplates = [];
          }

          // Export company statuses
          try {
            const companyStatusesRes = await fetch(`${API_URL}/company-statuses`);
            companyStatuses = companyStatusesRes.ok ? await companyStatusesRes.json() : [];
            console.log('Company statuses fetched:', companyStatuses.length);
          } catch (error) {
            console.warn('Company statuses not available:', error);
            companyStatuses = [];
          }

          // Export roles
          try {
            const rolesRes = await fetch(`${API_URL}/roles`);
            roles = rolesRes.ok ? await rolesRes.json() : [];
            console.log('Roles fetched:', roles.length);
          } catch (error) {
            console.warn('Roles not available:', error);
            roles = [];
          }

          // Export company transfers
          try {
            const companyTransfersRes = await fetch(`${API_URL}/company-transfers`);
            companyTransfers = companyTransfersRes.ok ? await companyTransfersRes.json() : [];
            console.log('Company transfers fetched:', companyTransfers.length);
          } catch (error) {
            console.warn('Company transfers not available:', error);
            companyTransfers = [];
          }

          // Export field mappings
          try {
            const fieldMappingsRes = await fetch(`${API_URL}/field-mappings`);
            fieldMappings = fieldMappingsRes.ok ? await fieldMappingsRes.json() : [];
            console.log('Field mappings fetched:', fieldMappings.length);
          } catch (error) {
            console.warn('Field mappings not available:', error);
            fieldMappings = [];
          }
        } catch (fetchError) {
          console.error('Error fetching admin data:', fetchError);
          console.error('🔍 ADMIN BACKUP DEBUG:', {
            errorName: fetchError.name,
            errorMessage: fetchError.message,
            API_URL,
            isAdmin,
            scope,
            timestamp: new Date().toISOString()
          });
          
          // Specifické chyby pro různé typy problémů
          if (fetchError.message.includes('Failed to fetch')) {
            throw new Error(`Nelze se připojit k serveru. Zkontrolujte internetové připojení a dostupnost backendu na ${API_URL.replace('/api', '')}`);
          } else if (fetchError.message.includes('CORS')) {
            throw new Error(`CORS chyba - problém s přístupem k API. Kontaktujte administrátora.`);
          } else {
            throw new Error(`Chyba při načítání admin dat: ${fetchError.message}`);
          }
        }
      } else {
        console.log('👤 USER BACKUP - fetching user data only');
        // Export pouze dat aktuálního uživatele
        const companiesRes = await fetch(`${API_URL}/companies?userId=${userId}&all=true`);
        const companiesResponse = await companiesRes.json();
        // Backend může vracet buď přímo array nebo objekt s companies property  
        companiesData = companiesResponse.companies || companiesResponse;
        console.log('User companies fetched:', companiesData.length);
        
        const usersRes = await fetch(`${API_URL}/users?userId=${userId || ''}`);
        const allUsers = await usersRes.json();
        usersData = allUsers.filter(user => user._id === userId);
        console.log('User data filtered:', usersData.length);

        // Pro běžného uživatele nezálohujeme novinky a zprávy
        newsData = [];
        messagesData = [];
        userKnowledgeData = [];
        knowledgeData = [];
        aiData = [];
        batchVisibility = [];
        requestTemplates = [];
        companyStatuses = [];
        roles = [];
        companyTransfers = [];
        fieldMappings = [];
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = scope === 'all' ? `crm-all-data-${timestamp}` : `crm-backup-${timestamp}`;

      if (format === 'excel') {
        await exportToExcel(companiesData, usersData, userKnowledgeData, knowledgeData, requestTemplates, companyStatuses, roles, filename);
      } else if (format === 'csv') {
        await exportToCSV(companiesData, usersData, userKnowledgeData, knowledgeData, requestTemplates, companyStatuses, roles, filename);
      } else {
        await exportToJSON(companiesData, usersData, newsData, messagesData, userKnowledgeData, knowledgeData, 
                          aiData, batchVisibility, requestTemplates, companyStatuses, roles, companyTransfers, fieldMappings, filename, scope);
      }

      const totalItems = (companiesData?.length || 0) + (usersData?.length || 0) + (newsData || []).length + (messagesData || []).length + 
                         (userKnowledgeData || []).length + (knowledgeData || []).length + (aiData || []).length + 
                         (batchVisibility || []).length + (requestTemplates || []).length + (companyStatuses || []).length + 
                         (roles || []).length + (companyTransfers || []).length + (fieldMappings || []).length;

      // Debug logování před alert
      console.log('=== ALERT DEBUG ===');
      console.log('companiesData:', companiesData, 'length:', companiesData?.length);
      console.log('aiData:', aiData, 'length:', aiData?.length);
      console.log('batchVisibility:', batchVisibility, 'length:', batchVisibility?.length);
      console.log('requestTemplates:', requestTemplates, 'length:', requestTemplates?.length);
      
      const companiesCount = companiesData?.length || 0;
      const usersCount = usersData?.length || 0;
      const newsCount = (newsData || []).length;
      const messagesCount = (messagesData || []).length;
      const aiCount = (aiData || []).length;
      const batchCount = (batchVisibility || []).length;
      const templatesCount = (requestTemplates || []).length;
      const statusesCount = (companyStatuses || []).length;
      const rolesCount = (roles || []).length;
      const transfersCount = (companyTransfers || []).length;
      const mappingsCount = (fieldMappings || []).length;
      
      alert(`${scope === 'all' ? 'Kompletní export' : 'Záloha'} byla úspěšně vytvořena a stažena!\n\nObsah (${totalItems} záznamů):\n- Firmy: ${companiesCount}\n- Uživatelé: ${usersCount}\n- Novinky: ${newsCount}\n- Zprávy: ${messagesCount}\n- AI konverzace: ${aiCount}\n- Viditelnost šarží: ${batchCount}\n- Šablony požadavků: ${templatesCount}\n- Stavy firem: ${statusesCount}\n- Role: ${rolesCount}\n- Přenosy firem: ${transfersCount}\n- Mapování polí: ${mappingsCount}`);
    } catch (error) {
      console.error('Chyba při vytváření zálohy:', error);
      alert('Nepodařilo se vytvořit zálohu: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToJSON = async (companiesData, usersData, newsData, messagesData, userKnowledgeData, knowledgeData, 
                               aiData, batchVisibility, requestTemplates, companyStatuses, roles, companyTransfers, fieldMappings, filename, scope) => {
    console.log('=== EXPORT TO JSON DEBUG ===');
    console.log('Companies to export:', companiesData.length);
    console.log('Users to export:', usersData.length);
    console.log('News to export:', (newsData || []).length);
    console.log('Messages to export:', (messagesData || []).length);
    console.log('User knowledge to export:', (userKnowledgeData || []).length);
    console.log('Knowledge structure to export:', (knowledgeData || []).length);
    console.log('AI data to export:', (aiData || []).length);
    console.log('Batch visibility to export:', (batchVisibility || []).length);
    console.log('Request templates to export:', (requestTemplates || []).length);
    console.log('Company statuses to export:', (companyStatuses || []).length);
    console.log('Roles to export:', (roles || []).length);
    console.log('Company transfers to export:', (companyTransfers || []).length);
    console.log('Field mappings to export:', (fieldMappings || []).length);
    console.log('Scope:', scope);
    
    // Zobraz prvních 3 firmy a jejich vlastníky
    if (companiesData.length > 0) {
      console.log('First 3 companies:', companiesData.slice(0, 3).map(c => ({
        name: c.name,
        createdBy: c.createdBy?._id || c.createdBy
      })));
    }
    
    const backup = {
      version: "2.0", // Zvýšená verze pro kompletní datové typy
      timestamp: new Date().toISOString(),
      userId: userId,
      scope: scope,
      data: {
        companies: companiesData,
        users: usersData,
        news: newsData || [],
        messages: messagesData || [],
        userKnowledge: userKnowledgeData || [],
        knowledge: knowledgeData || [],
        aiData: aiData || [],
        batchVisibility: batchVisibility || [],
        requestTemplates: requestTemplates || [],
        companyStatuses: companyStatuses || [],
        roles: roles || [],
        companyTransfers: companyTransfers || [],
        fieldMappings: fieldMappings || []
      },
      metadata: {
        totalCompanies: companiesData.length,
        totalUsers: usersData.length,
        totalNews: (newsData || []).length,
        totalMessages: (messagesData || []).length,
        totalUserKnowledge: (userKnowledgeData || []).length,
        totalKnowledge: (knowledgeData || []).length,
        totalAiData: (aiData || []).length,
        totalBatchVisibility: (batchVisibility || []).length,
        totalRequestTemplates: (requestTemplates || []).length,
        totalCompanyStatuses: (companyStatuses || []).length,
        totalRoles: (roles || []).length,
        totalCompanyTransfers: (companyTransfers || []).length,
        totalFieldMappings: (fieldMappings || []).length,
        exportedBy: userId,
        appVersion: "CRM v2.0"
      }
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    downloadFile(blob, `${filename}.json`);
  };

  const exportToExcel = async (companiesData, usersData, userKnowledgeData, knowledgeData, requestTemplates, companyStatuses, roles, filename) => {
    const workbook = XLSX.utils.book_new();

    // Vytvoř mapu userId -> userName pro rychlé vyhledávání
    const userMap = {};
    (usersData || []).forEach(user => {
      userMap[user._id] = user.name;
    });

    // Sheet pro firmy
    const companiesSheet = companiesData.map(company => ({
      'IČO': company.ico,
      'Název firmy': company.name,
      'Adresa': company.address,
      'BMSL': company.bmsl || '',
      'Forecast': company.forecast || '',
      'Status': company.status || '',
      'Operátor': company.operator || '',
      'Vlastník': userMap[company.userId] || 'Neznámý uživatel',
      'Datum ukončení RS': company.rsEndDate || '',
      'Nepropojovat': company.doNotConnect || false,
      'Útrata': company.expenditure || '',
      'Provize': company.commission || '',
      'Vytvořeno': company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '',
      'Kontakty': company.contacts?.map(c => `${c.name} (${c.phone}, ${c.email})`).join('; ') || '',
      'Počet aktivit': company.activities?.length || 0
    }));
    
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(companiesSheet), 'Firmy');

    // Sheet pro uživatele
    const usersSheet = usersData.map(user => ({
      'Jméno': user.name,
      'Email': user.email,
      'Role': user.role,
      'Vytvořeno': user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''
    }));
    
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(usersSheet), 'Uživatelé');

    // Pokud jsou k dispozici aktivity, přidej je také
    const allActivities = [];
    companiesData.forEach(company => {
      if (company.activities && company.activities.length > 0) {
        company.activities.forEach(activity => {
          allActivities.push({
            'Firma': company.name,
            'IČO': company.ico,
            'Text aktivity': activity.text,
            'Datum': activity.date ? new Date(activity.date).toLocaleDateString() : '',
            'Čas': activity.time || '',
            'Vytvořeno': activity.createdAt ? new Date(activity.createdAt).toLocaleDateString() : ''
          });
        });
      }
    });

    if (allActivities.length > 0) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(allActivities), 'Aktivity');
    }

    // Sheet pro uživatelské znalosti
    if (userKnowledgeData && userKnowledgeData.length > 0) {
      const userKnowledgeSheet = userKnowledgeData.map(uk => ({
        'Uživatel': uk.userId?.name || 'Neznámý',
        'Email uživatele': uk.userId?.email || '',
        'Znalost': uk.knowledgeId?.name || 'Neznámá znalost',
        'Typ': uk.knowledgeId?.type || '',
        'Má znalost': uk.hasSkill ? 'Ano' : 'Ne',
        'Úroveň': uk.proficiencyLevel || '',
        'Poznámky': uk.notes || '',
        'Vytvořeno': uk.createdAt ? new Date(uk.createdAt).toLocaleDateString() : '',
        'Aktualizováno': uk.lastUpdated ? new Date(uk.lastUpdated).toLocaleDateString() : '',
        'Aktualizoval': uk.updatedBy?.name || ''
      }));
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(userKnowledgeSheet), 'Uživatelské znalosti');
    }

    // Sheet pro strukturu znalostí
    if (knowledgeData && knowledgeData.length > 0) {
      const knowledgeSheet = knowledgeData.map(k => ({
        'Název': k.name,
        'Typ': k.type,
        'Popis': k.description || '',
        'Barva': k.color || '',
        'Rodič': k.parentId || '',
        'Pořadí': k.order || 0,
        'Vytvořeno': k.createdAt ? new Date(k.createdAt).toLocaleDateString() : ''
      }));
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(knowledgeSheet), 'Struktura znalostí');
    }

    // Sheet pro request templates
    if (requestTemplates && requestTemplates.length > 0) {
      const templatesSheet = requestTemplates.map(template => ({
        'Název': template.name || '',
        'Typ': template.type || '',
        'Obsah': template.content || '',
        'Popis': template.description || '',
        'Aktivní': template.active ? 'Ano' : 'Ne',
        'Vytvořeno': template.createdAt ? new Date(template.createdAt).toLocaleDateString() : '',
        'Vytvořil': template.createdBy?.name || ''
      }));
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(templatesSheet), 'Request Templates');
    }

    // Sheet pro company statuses
    if (companyStatuses && companyStatuses.length > 0) {
      const statusesSheet = companyStatuses.map(status => ({
        'Název': status.name || '',
        'Popis': status.description || '',
        'Barva': status.color || '',
        'Pořadí': status.order || 0,
        'Aktivní': status.active ? 'Ano' : 'Ne',
        'Vytvořeno': status.createdAt ? new Date(status.createdAt).toLocaleDateString() : ''
      }));
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(statusesSheet), 'Stavy firem');
    }

    // Sheet pro roles
    if (roles && roles.length > 0) {
      const rolesSheet = roles.map(role => ({
        'Název': role.name || '',
        'Popis': role.description || '',
        'Oprávnění - Správa uživatelů': role.permissions?.manageUsers ? 'Ano' : 'Ne',
        'Oprávnění - Správa novinek': role.permissions?.manageNews ? 'Ano' : 'Ne',
        'Oprávnění - Správa zpráv': role.permissions?.manageMessages ? 'Ano' : 'Ne',
        'Oprávnění - Správa znalostí': role.permissions?.manageKnowledge ? 'Ano' : 'Ne',
        'Oprávnění - Export dat': role.permissions?.exportData ? 'Ano' : 'Ne',
        'Oprávnění - Import dat': role.permissions?.importData ? 'Ano' : 'Ne',
        'Oprávnění - Správa systému': role.permissions?.systemAdmin ? 'Ano' : 'Ne',
        'Skrytá': role.hidden ? 'Ano' : 'Ne',
        'Vytvořeno': role.createdAt ? new Date(role.createdAt).toLocaleDateString() : ''
      }));
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rolesSheet), 'Role');
    }

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadFile(blob, `${filename}.xlsx`);
  };

  const exportToCSV = async (companiesData, usersData, userKnowledgeData, knowledgeData, requestTemplates, companyStatuses, roles, filename) => {
    // Vytvoř mapu userId -> userName pro rychlé vyhledávání
    const userMap = {};
    (usersData || []).forEach(user => {
      userMap[user._id] = user.name;
    });

    const csvData = companiesData.map(company => ({
      'ico': company.ico,
      'name': company.name,
      'address': company.address,
      'bmsl': company.bmsl || '',
      'forecast': company.forecast || '',
      'status': company.status || '',
      'operator': company.operator || '',
      'owner': userMap[company.userId] || 'Neznámý uživatel',
      'rsEndDate': company.rsEndDate || '',
      'doNotConnect': company.doNotConnect || false,
      'expenditure': company.expenditure || '',
      'commission': company.commission || '',
      'createdAt': company.createdAt || '',
      'contacts': company.contacts ? JSON.stringify(company.contacts) : '',
      'activities': company.activities ? JSON.stringify(company.activities) : ''
    }));

    const csv = convertToCSV(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `${filename}.csv`);
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma or quote
          return value.toString().includes(',') || value.toString().includes('"') 
            ? `"${value.toString().replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');
    
    return csvContent;
  };

  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setImportFile(file);
  };

  const restoreBackup = async () => {
    if (!importFile) {
      alert('Prosím vyberte soubor se zálohou');
      return;
    }

    setIsImporting(true);
    try {
      const fileExtension = importFile.name.split('.').pop().toLowerCase();
      
      if (fileExtension === 'json') {
        await restoreFromJSON();
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        await restoreFromExcel();
      } else if (fileExtension === 'csv') {
        await restoreFromCSV();
      } else {
        throw new Error('Nepodporovaný formát souboru. Podporované jsou: JSON, Excel (.xlsx), CSV');
      }

      alert('Data byla úspěšně obnovena!');
      if (onRestore) onRestore();
    } catch (error) {
      console.error('Chyba při obnovování dat:', error);
      alert('Nepodařilo se obnovit data: ' + error.message);
    } finally {
      setIsImporting(false);
      setImportFile(null);
    }
  };

  const restoreFromJSON = async () => {
    const text = await importFile.text();
    const backup = JSON.parse(text);

    // Validace struktury zálohy
    if (!backup.version || !backup.data || !backup.data.companies) {
      throw new Error('Neplatný formát zálohy');
    }

    // Zjisti verzi zálohy pro inteligentní import
    const backupVersion = parseFloat(backup.version) || 1.0;
    const isOldBackup = backupVersion < 2.0;
    
    // Upraví varování podle verze zálohy
    let warningMessage = `Opravdu chcete obnovit zálohu z ${new Date(backup.timestamp).toLocaleString()}?\n\n`;
    
    if (isOldBackup) {
      warningMessage += `⚠️ STARŠÍ ZÁLOHA (verze ${backup.version}):\n` +
        `Tato záloha neobsahuje všechna moderní data.\n` +
        `Zachovají se současná AI data, šablony, role apod.\n\n`;
    } else {
      warningMessage += `📋 KOMPLETNÍ ZÁLOHA (verze ${backup.version}):\n` +
        `Obsahuje všechna systémová data.\n\n`;
    }
    
    // Varování před obnovením
    warningMessage += `Záloha obsahuje:\n` +
      `• ${backup.data.companies.length} firem\n` +
      `• ${(backup.data.news || []).length} novinek\n` +
      `• ${(backup.data.messages || []).length} zpráv\n`;
    
    // Přidej info o nových datech pouze pokud existují v záloze
    if (!isOldBackup || backup.data.aiData) {
      warningMessage += `• ${(backup.data.aiData || []).length} AI dat\n`;
    }
    if (!isOldBackup || backup.data.batchVisibility) {
      warningMessage += `• ${(backup.data.batchVisibility || []).length} batch visibility\n`;
    }
    if (!isOldBackup || backup.data.requestTemplates) {
      warningMessage += `• ${(backup.data.requestTemplates || []).length} request templates\n`;
    }
    if (!isOldBackup || backup.data.companyStatuses) {
      warningMessage += `• ${(backup.data.companyStatuses || []).length} company statuses\n`;
    }
    if (!isOldBackup || backup.data.roles) {
      warningMessage += `• ${(backup.data.roles || []).length} rolí\n`;
    }
    if (!isOldBackup || backup.data.companyTransfers) {
      warningMessage += `• ${(backup.data.companyTransfers || []).length} company transfers\n`;
    }
    if (!isOldBackup || backup.data.fieldMappings) {
      warningMessage += `• ${(backup.data.fieldMappings || []).length} field mappings\n`;
    }
    
    warningMessage += `\n⚠️ POZOR: `;
    if (isOldBackup) {
      warningMessage += `Budou přepsána pouze data obsažená v záloze.\nNová data (AI, šablony, atd.) zůstanou zachována.`;
    } else {
      warningMessage += `Toto KOMPLETNĚ PŘEPÍŠE všechna vaše současná data!\nVšechna existující data budou SMAZÁNA a nahrazena daty ze zálohy.`;
    }
    warningMessage += `\nTato operace je NEVRATNÁ!`;
    
    const confirmRestore = window.confirm(warningMessage);

    if (!confirmRestore) return;

    // Nejprve smaž existující firmy uživatele
    const existingCompanies = await fetch(`${API_URL}/companies?userId=${userId}`);
    const existingData = await existingCompanies.json();
    
    for (const company of existingData) {
      await fetch(`${API_URL}/companies/${company._id}`, {
        method: "DELETE"
      });
    }

    // Obnovit firmy
    for (const company of backup.data.companies) {
      try {
        // Odstraň _id pro vytvoření nové firmy
        const { _id, ...companyData } = company;
        companyData.userId = userId; // Přiřaď aktuálnímu uživateli

        const response = await fetch(`${API_URL}/companies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          console.error(`Nepodařilo se obnovit firmu ${company.name}`);
        }
      } catch (error) {
        console.error(`Chyba při obnovování firmy ${company.name}:`, error);
      }
    }

    // Obnovit novinky (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.news && backup.data.news.length > 0) {
      // Nejprve smaž existující novinky (pouze admin)
      const existingNews = await fetch(`${API_URL}/news`);
      const existingNewsData = await existingNews.json();
      
      for (const news of existingNewsData) {
        await fetch(`${API_URL}/news/${news._id}`, {
          method: "DELETE"
        });
      }

      // Obnov novinky
      for (const news of backup.data.news) {
        try {
          const { _id, ...newsData } = news;
          await fetch(`${API_URL}/news`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newsData),
          });
        } catch (error) {
          console.error(`Chyba při obnovování novinky:`, error);
        }
      }
    }

    // Obnovit AI data (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.aiData && backup.data.aiData.length > 0) {
      // Smaž existující AI data pouze pokud záloha obsahuje AI data (novější zálohy)
      if (!isOldBackup || backup.data.aiData.length > 0) {
        try {
          const existingAiData = await fetch(`${API_URL}/ai-data`);
          if (existingAiData.ok) {
            const existingAiDataList = await existingAiData.json();
            for (const aiItem of existingAiDataList) {
              await fetch(`${API_URL}/ai-data/${aiItem._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících AI dat:`, error);
        }
      }

      try {
        for (const aiItem of backup.data.aiData) {
          const { _id, ...aiData } = aiItem;
          await fetch(`${API_URL}/ai-data`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(aiData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování AI dat:`, error);
      }
    }

    // Obnovit batch visibility (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.batchVisibility && backup.data.batchVisibility.length > 0) {
      // Smaž existující batch visibility pouze pokud záloha obsahuje tato data
      if (!isOldBackup || backup.data.batchVisibility.length > 0) {
        try {
          const existingBatchVisibility = await fetch(`${API_URL}/batch-visibility`);
          if (existingBatchVisibility.ok) {
            const existingBatches = await existingBatchVisibility.json();
            for (const batch of existingBatches) {
              await fetch(`${API_URL}/batch-visibility/${batch._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících batch visibility:`, error);
        }
      }

      try {
        for (const batch of backup.data.batchVisibility) {
          const { _id, ...batchData } = batch;
          await fetch(`${API_URL}/batch-visibility`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(batchData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování batch visibility:`, error);
      }
    }

    // Obnovit request templates (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.requestTemplates && backup.data.requestTemplates.length > 0) {
      // Smaž existující request templates pouze pokud záloha obsahuje tato data
      if (!isOldBackup || backup.data.requestTemplates.length > 0) {
        try {
          const existingTemplates = await fetch(`${API_URL}/request-templates`);
          if (existingTemplates.ok) {
            const existingTemplatesList = await existingTemplates.json();
            for (const template of existingTemplatesList) {
              await fetch(`${API_URL}/request-templates/${template._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících request templates:`, error);
        }
      }

      try {
        for (const template of backup.data.requestTemplates) {
          const { _id, ...templateData } = template;
          await fetch(`${API_URL}/request-templates`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(templateData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování request templates:`, error);
      }
    }

    // Obnovit company statuses (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.companyStatuses && backup.data.companyStatuses.length > 0) {
      // Smaž existující company statuses pouze pokud záloha obsahuje tato data
      if (!isOldBackup || backup.data.companyStatuses.length > 0) {
        try {
          const existingStatuses = await fetch(`${API_URL}/company-statuses`);
          if (existingStatuses.ok) {
            const existingStatusesList = await existingStatuses.json();
            for (const status of existingStatusesList) {
              await fetch(`${API_URL}/company-statuses/${status._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících company statuses:`, error);
        }
      }

      try {
        for (const status of backup.data.companyStatuses) {
          const { _id, ...statusData } = status;
          await fetch(`${API_URL}/company-statuses`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(statusData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování company statuses:`, error);
      }
    }

    // Obnovit roles (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.roles && backup.data.roles.length > 0) {
      // Smaž existující custom roles pouze pokud záloha obsahuje role data
      if (!isOldBackup || (backup.data.roles && backup.data.roles.length > 0)) {
        try {
          const existingRoles = await fetch(`${API_URL}/roles`);
          if (existingRoles.ok) {
            const existingRolesList = await existingRoles.json();
            // Základní systémové role, které nesmíme smazat
            const systemRoles = ['admin', 'user', 'editor', 'viewer', 'database'];
            
            for (const role of existingRolesList) {
              if (!systemRoles.includes(role.name)) {
                await fetch(`${API_URL}/roles/${role._id}`, {
                  method: "DELETE"
                });
              }
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících custom roles:`, error);
        }
      }

      try {
        for (const role of backup.data.roles) {
          const { _id, ...roleData } = role;
          await fetch(`${API_URL}/roles`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(roleData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování roles:`, error);
      }
    }

    // Obnovit company transfers (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.companyTransfers && backup.data.companyTransfers.length > 0) {
      // Smaž existující company transfers pouze pokud záloha obsahuje tato data
      if (!isOldBackup || (backup.data.companyTransfers && backup.data.companyTransfers.length > 0)) {
        try {
          const existingTransfers = await fetch(`${API_URL}/company-transfers`);
          if (existingTransfers.ok) {
            const existingTransfersList = await existingTransfers.json();
            for (const transfer of existingTransfersList) {
              await fetch(`${API_URL}/company-transfers/${transfer._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících company transfers:`, error);
        }
      }

      try {
        for (const transfer of backup.data.companyTransfers) {
          const { _id, ...transferData } = transfer;
          await fetch(`${API_URL}/company-transfers`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(transferData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování company transfers:`, error);
      }
    }

    // Obnovit field mappings (pouze pro admin a pokud existují v záloze)
    if (isAdmin && backup.data.fieldMappings && backup.data.fieldMappings.length > 0) {
      // Smaž existující field mappings pouze pokud záloha obsahuje tato data
      if (!isOldBackup || (backup.data.fieldMappings && backup.data.fieldMappings.length > 0)) {
        try {
          const existingMappings = await fetch(`${API_URL}/field-mappings`);
          if (existingMappings.ok) {
            const existingMappingsList = await existingMappings.json();
            for (const mapping of existingMappingsList) {
              await fetch(`${API_URL}/field-mappings/${mapping._id}`, {
                method: "DELETE"
              });
            }
          }
        } catch (error) {
          console.error(`Chyba při mazání existujících field mappings:`, error);
        }
      }

      try {
        for (const mapping of backup.data.fieldMappings) {
          const { _id, ...mappingData } = mapping;
          await fetch(`${API_URL}/field-mappings`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(mappingData),
          });
        }
      } catch (error) {
        console.error(`Chyba při obnovování field mappings:`, error);
      }
    }

    // Poznámka: Zprávy se z bezpečnostních důvodů neobnovují automaticky
  };

  const restoreFromExcel = async () => {
    const data = await importFile.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    
    const firmsSheet = workbook.Sheets['Firmy'];
    if (!firmsSheet) {
      throw new Error('Excel soubor neobsahuje sheet "Firmy"');
    }

    const firmsData = XLSX.utils.sheet_to_json(firmsSheet);
    
    const confirmRestore = window.confirm(
      `Opravdu chcete importovat ${firmsData.length} firem z Excel souboru?\n` +
      `POZOR: Toto přepíše všechna vaše současná data!`
    );

    if (!confirmRestore) return;

    // Nejprve smaž existující firmy uživatele
    const existingCompanies = await fetch(`${API_URL}/companies?userId=${userId}`);
    const existingData = await existingCompanies.json();
    
    for (const company of existingData) {
      await fetch(`${API_URL}/companies/${company._id}`, {
        method: "DELETE"
      });
    }

    // Import firem z Excel
    for (const row of firmsData) {
      try {
        const companyData = {
          ico: row['IČO'] || '',
          name: row['Název firmy'] || '',
          address: row['Adresa'] || '',
          bmsl: row['BMSL'] || '',
          forecast: row['Forecast'] || '',
          status: row['Status'] || '',
          operator: row['Operátor'] || '',
          rsEndDate: row['Datum ukončení RS'] || '',
          expenditure: row['Útrata'] || '',
          commission: row['Provize'] || '',
          contacts: row['Kontakty'] ? parseContactsFromString(row['Kontakty']) : [],
          activities: [],
          userId: userId
        };

        const response = await fetch(`${API_URL}/companies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          console.error(`Nepodařilo se importovat firmu ${row['Název firmy']}`);
        }
      } catch (error) {
        console.error(`Chyba při importu firmy ${row['Název firmy']}:`, error);
      }
    }
  };

  const restoreFromCSV = async () => {
    const text = await importFile.text();
    const lines = text.split('\n');
    const headers = lines[0].split(',');
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index] ? values[index].trim() : '';
        });
        data.push(row);
      }
    }

    const confirmRestore = window.confirm(
      `Opravdu chcete importovat ${data.length} firem z CSV souboru?\n` +
      `POZOR: Toto přepíše všechna vaše současná data!`
    );

    if (!confirmRestore) return;

    // Nejprve smaž existující firmy uživatele
    const existingCompanies = await fetch(`${API_URL}/companies?userId=${userId}`);
    const existingData = await existingCompanies.json();
    
    for (const company of existingData) {
      await fetch(`${API_URL}/companies/${company._id}`, {
        method: "DELETE"
      });
    }

    // Import firem z CSV
    for (const row of data) {
      try {
        const companyData = {
          ico: row['ico'] || '',
          name: row['name'] || '',
          address: row['address'] || '',
          bmsl: row['bmsl'] || '',
          forecast: row['forecast'] || '',
          status: row['status'] || '',
          operator: row['operator'] || '',
          rsEndDate: row['rsEndDate'] || '',
          expenditure: row['expenditure'] || '',
          commission: row['commission'] || '',
          contacts: row['contacts'] ? JSON.parse(row['contacts']) : [],
          activities: row['activities'] ? JSON.parse(row['activities']) : [],
          userId: userId
        };

        const response = await fetch(`${API_URL}/companies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          console.error(`Nepodařilo se importovat firmu ${row['name']}`);
        }
      } catch (error) {
        console.error(`Chyba při importu firmy ${row['name']}:`, error);
      }
    }
  };

  const parseContactsFromString = (contactsStr) => {
    // Parsing kontaktů z formátu "Jméno (telefon, email); Jméno2 (telefon2, email2)"
    const contacts = [];
    const contactParts = contactsStr.split(';');
    
    contactParts.forEach(part => {
      const match = part.trim().match(/^(.+?)\s*\((.+?),\s*(.+?)\)$/);
      if (match) {
        contacts.push({
          name: match[1].trim(),
          phone: match[2].trim(),
          email: match[3].trim()
        });
      }
    });
    
    return contacts;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    
    return result;
  };

  const createPartialBackup = async (filteredCompanies) => {
    setIsExporting(true);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `crm-partial-export-${timestamp}`;
      
      // Pro rychlý export použiji Excel formát místo JSON pro lepší čitelnost
      await exportToExcel(filteredCompanies, users, filename);

      alert(`Částečný export byl vytvořen! (${filteredCompanies.length} firem)`);
    } catch (error) {
      console.error('Chyba při vytváření částečného exportu:', error);
      alert('Nepodařilo se vytvořit export: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  // Zobrazit náhled problémů s forecast hodnotami
  const previewForecastIssues = async () => {
    if (!isAdmin) {
      alert('Pouze administrátor může zobrazit náhled problémů.');
      return;
    }

    setIsLoading(true);
    try {
      // Načíst všechna data ze všech uživatelů
      console.log('🔥 ADMIN FORECAST PREVIEW - fetching all data');
      const companiesRes = await fetch(`${API_URL}/companies?userId=${userId || ''}`);
      if (!companiesRes.ok) {
        throw new Error(`Companies fetch failed: ${companiesRes.status} ${companiesRes.statusText}`);
      }
      const allCompanies = await companiesRes.json();
      console.log('All companies fetched for forecast preview:', allCompanies.length);

      if (!allCompanies || allCompanies.length === 0) {
        alert('❌ Žádná data firem nejsou k dispozici.');
        return;
      }

      const issues = [];
      const allForecasts = new Set();

      console.log('🔍 DEBUG: Kontroluji forecast hodnoty...');
      console.log('Celkem firem:', allCompanies.length);

      // Najít všechny problémové forecast hodnoty a všechny unikátní hodnoty
      for (const company of allCompanies) {
        if (company.forecast !== undefined && company.forecast !== null && company.forecast !== "") {
          const forecast = String(company.forecast).trim();
          allForecasts.add(forecast);
          console.log(`Firma: ${company.name}, Forecast: "${forecast}" (typ: ${typeof company.forecast})`);
          
          let issue = null;
          
          // Normalizace forecast hodnot - sjednocení na čísla bez %
          const forecastStr = String(forecast).trim();
          
          // Ošetření všech možných variant
          if (forecastStr === "0%" || forecastStr === "0 %" || forecastStr === "0" || forecast === 0) {
            issue = { type: 'format', current: forecast, suggested: '0' };
          } else if (forecastStr === "25%" || forecastStr === "25 %" || forecastStr === "25" || forecast === 25) {
            issue = { type: 'format', current: forecast, suggested: '25' };
          } else if (forecastStr === "50%" || forecastStr === "50 %" || forecastStr === "50" || forecast === 50) {
            issue = { type: 'format', current: forecast, suggested: '50' };
          } else if (forecastStr === "75%" || forecastStr === "75 %" || forecastStr === "75" || forecast === 75) {
            issue = { type: 'format', current: forecast, suggested: '75' };
          } else if (forecastStr === "99%" || forecastStr === "99 %" || forecastStr === "99" || forecast === 99) {
            issue = { type: 'format', current: forecast, suggested: '99' };
          } else if (forecastStr === "100%" || forecastStr === "100 %" || forecastStr === "100" || forecast === 100) {
            issue = { type: 'format', current: forecast, suggested: '100' };
          } else if (forecastStr === "Forecast" || forecastStr === "forecast") {
            issue = { type: 'invalid', current: forecast, suggested: '0' };
          } else if (forecastStr.includes('%') || forecastStr.includes(' %')) {
            // Automaticky vyčistit jakoukoliv hodnotu s % nebo ' %'
            const cleanValue = forecastStr.replace(/%/g, '').replace(/\s+%/g, '').trim();
            if (!isNaN(cleanValue) && cleanValue !== '' && cleanValue !== forecastStr) {
              issue = { type: 'format', current: forecast, suggested: cleanValue };
            }
          }

          if (issue) {
            issues.push({
              company: company.name,
              ico: company.ico,
              ...issue
            });
            console.log(`✅ Problém nalezen: "${forecast}" → "${issue.suggested}"`);
          }
        }
      }

      // Zobrazit všechny unikátní forecast hodnoty
      const uniqueForecastsArray = Array.from(allForecasts).sort();
      console.log('🔍 DEBUG: Všechny unikátní forecast hodnoty:', uniqueForecastsArray);
      console.log('🔍 DEBUG: Celkem problémů nalezeno:', issues.length);

      // Zobrazit alert s unikátními hodnotami
      const uniqueMessage = `📊 Všechny unikátní forecast hodnoty v databázi (${uniqueForecastsArray.length}):\n\n` +
        uniqueForecastsArray.map(f => `"${f}"`).join(', ') + 
        `\n\n🔍 Nalezeno ${issues.length} problémů s forecast hodnotami:\n\n` +
        (issues.length > 0 ? 
          issues.slice(0, 15).map(issue => 
            `${issue.company} (${issue.ico}): "${issue.current}" → "${issue.suggested}"`
          ).join('\n') +
          (issues.length > 15 ? `\n... a další ${issues.length - 15} problémů` : '')
          : "Žádné problémy nebyly nalezeny podle současných pravidel.") +
        '\n\nChcete upravit pravidla nebo opravit nalezené problémy?';

      alert(uniqueMessage);

    } catch (error) {
      console.error('Chyba při náhledu forecast problémů:', error);
      alert(`❌ Chyba při náhledu: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk oprava forecast hodnot
  const bulkFixForecastValues = async () => {
    if (!isAdmin) {
      alert('Pouze administrátor může provádět bulk opravy dat.');
      return;
    }

    if (!window.confirm('Opravdu chcete opravit všechny nekonzistentní forecast hodnoty? Tato akce je nevratná!')) {
      return;
    }

    setIsLoading(true);
    try {
      // Načíst všechna data ze všech uživatelů
      console.log('🔥 ADMIN FORECAST FIX - fetching all data');
      const companiesRes = await fetch(`${API_URL}/companies?userId=${userId || ''}`);
      if (!companiesRes.ok) {
        throw new Error(`Companies fetch failed: ${companiesRes.status} ${companiesRes.statusText}`);
      }
      const allCompanies = await companiesRes.json();
      console.log('All companies fetched for forecast fix:', allCompanies.length);

      if (!allCompanies || allCompanies.length === 0) {
        alert('❌ Žádná data firem nejsou k dispozici.');
        return;
      }

      let fixedCount = 0;
      const updates = [];

      console.log('🔧 DEBUG: Začínám bulk opravu forecast hodnot...');
      console.log('Celkem firem:', allCompanies.length);

      // Projít všechny firmy a najít ty s nekonzistentními forecast hodnotami
      for (const company of allCompanies) {
        let needsUpdate = false;
        let newForecast = company.forecast;

        if (company.forecast !== undefined && company.forecast !== null) {
          const forecastStr = String(company.forecast).trim();
          
          // Oprava různých formátů - normalizace na čísla bez %
          if (forecastStr === "0%" || forecastStr === "0 %" || forecastStr === "0" || company.forecast === 0) {
            newForecast = "0";
            needsUpdate = true;
          } else if (forecastStr === "25%" || forecastStr === "25 %" || forecastStr === "25" || company.forecast === 25) {
            newForecast = "25";
            needsUpdate = true;
          } else if (forecastStr === "50%" || forecastStr === "50 %" || forecastStr === "50" || company.forecast === 50) {
            newForecast = "50";
            needsUpdate = true;
          } else if (forecastStr === "75%" || forecastStr === "75 %" || forecastStr === "75" || company.forecast === 75) {
            newForecast = "75";
            needsUpdate = true;
          } else if (forecastStr === "99%" || forecastStr === "99 %" || forecastStr === "99" || company.forecast === 99) {
            newForecast = "99";
            needsUpdate = true;
          } else if (forecastStr === "100%" || forecastStr === "100 %" || forecastStr === "100" || company.forecast === 100) {
            newForecast = "100";
            needsUpdate = true;
          } else if (forecastStr === "Forecast" || forecastStr === "forecast") {
            newForecast = "0";
            needsUpdate = true;
          } else if (forecastStr.includes('%') || forecastStr.includes(' %')) {
            // Automaticky odstranit % ze všech hodnot
            const cleanValue = forecastStr.replace(/%/g, '').replace(/\s+%/g, '').trim();
            if (!isNaN(cleanValue) && cleanValue !== '' && cleanValue !== forecastStr) {
              newForecast = cleanValue;
              needsUpdate = true;
            }
          }
        }

        if (needsUpdate) {
          updates.push({
            _id: company._id,
            name: company.name,
            oldForecast: company.forecast,
            newForecast: newForecast
          });
          console.log(`🔧 Oprava: ${company.name}: "${company.forecast}" → "${newForecast}"`);
        }
      }

      console.log('🔧 DEBUG: Celkem aktualizací k provedení:', updates.length);

      if (updates.length === 0) {
        alert('Nebyly nalezeny žádné nekonzistentní forecast hodnoty k opravě.');
        return;
      }

      // Zobrazit souhrn změn
      const confirmMessage = `Nalezeno ${updates.length} firem s nekonzistentními forecast hodnotami:\n\n` +
        updates.slice(0, 10).map(u => `${u.name}: "${u.oldForecast}" → "${u.newForecast}"`).join('\n') +
        (updates.length > 10 ? `\n... a další ${updates.length - 10} firem` : '') +
        '\n\nPokračovat s opravou?';

      if (!window.confirm(confirmMessage)) return;

      // Provést bulk update
      console.log('🔧 DEBUG: Začínám API aktualizace...');
      for (const update of updates) {
        console.log(`🔧 Aktualizuji firmu: ${update.name} (${update._id})`);
        console.log(`🔧 URL: ${API_URL}/companies/${update._id}`);
        console.log(`🔧 Data:`, { forecast: update.newForecast });
        
        const updateResponse = await fetch(`${API_URL}/companies/${update._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ forecast: update.newForecast })
        });

        console.log(`🔧 Response status: ${updateResponse.status}`);
        
        if (updateResponse.ok) {
          fixedCount++;
          console.log(`✅ Úspěšně aktualizováno: ${update.name}`);
        } else {
          const errorText = await updateResponse.text();
          console.error(`❌ Chyba při aktualizaci firmy ${update.name}:`, updateResponse.status, errorText);
        }
      }

      console.log(`🔧 DEBUG: Celkem opraveno: ${fixedCount} z ${updates.length}`);

      alert(`✅ Úspěšně opraveno ${fixedCount} forecast hodnot z ${updates.length} firem!`);
      
      // Refresh dat
      if (onRestore) {
        onRestore();
      }

    } catch (error) {
      console.error('Chyba při bulk opravě forecast hodnot:', error);
      alert(`❌ Chyba při opravě forecast hodnot: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10`}>
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        <PageHeader
          title="📦 Zálohování a obnova dat"
        />
        
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow space-y-6`}>
          <div>
        
        {/* Export Controls */}
        <div className={`${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50'} p-4 rounded-lg mb-4`}>
          <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>Exportovat data</h4>
          
          {/* DEBUG informace pro admin */}
          {isAdmin && (
            <div className={`${isDarkMode ? 'bg-yellow-900/30 text-yellow-200 border border-yellow-700' : 'bg-yellow-100 text-yellow-800'} p-2 rounded text-xs mb-3`}>
              <strong>DEBUG:</strong> isAdmin={isAdmin.toString()}, exportScope="{exportScope}", 
              podmínka pro všechna data: {(exportScope === 'all' && isAdmin).toString()}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Formát exportu:</label>
              <select 
                value={exportFormat} 
                onChange={(e) => setExportFormat(e.target.value)}
                className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="json">JSON (záloha)</option>
                <option value="excel">Excel (.xlsx)</option>
                <option value="csv">CSV</option>
              </select>
            </div>
            
            {isAdmin && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Rozsah exportu:
                  <span className={`${isDarkMode ? 'text-red-400' : 'text-red-600'} font-bold ml-2`}>
                    ⚠️ Pro všechna data vyberte "Všechna data (admin)"!
                  </span>
                </label>
                <select 
                  value={exportScope} 
                  onChange={(e) => setExportScope(e.target.value)}
                  className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="current">Pouze moje data</option>
                  <option value="all">Všechna data (admin) ⭐</option>
                </select>
              </div>
            )}
          </div>

          <button
            onClick={() => createBackup()}
            disabled={isExporting}
            className={`px-4 py-2 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isExporting ? '🔄 Exportuji...' : `💾 Exportovat (${exportFormat.toUpperCase()})`}
          </button>
        </div>

        {/* Import Controls */}
        <div className={`${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50'} p-4 rounded-lg mb-4`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-yellow-200' : 'text-yellow-900'}`}>Importovat / Obnovit data</h4>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            ⚠️ POZOR: Toto přepíše všechna vaše současná data!
          </p>
          <div className="space-y-3">
            <input
              type="file"
              accept=".json,.xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className={`block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold ${
                isDarkMode
                  ? 'text-gray-300 file:bg-yellow-800 file:text-yellow-200 hover:file:bg-yellow-700'
                  : 'text-gray-500 file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100'
              }`}
            />
            <button
              onClick={restoreBackup}
              disabled={!importFile || isImporting}
              className={`px-4 py-2 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode 
                  ? 'bg-yellow-600 hover:bg-yellow-700' 
                  : 'bg-yellow-500 hover:bg-yellow-600'
              }`}
            >
              {isImporting ? '🔄 Importuji...' : '🔧 Importovat data'}
            </button>
          </div>
        </div>

        {/* Částečná záloha aktuálních firem */}
        <div className={`${isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50'} p-4 rounded-lg`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-green-200' : 'text-green-900'}`}>Rychlý export zobrazených firem</h4>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Exportuje pouze firmy, které jsou aktuálně zobrazené (včetně filtrů).
          </p>
          <button
            onClick={() => createPartialBackup(companies || [])}
            disabled={isExporting || (companies?.length || 0) === 0}
            className={`px-4 py-2 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-green-500 hover:bg-green-600'
            }`}
          >
            {isExporting ? '🔄 Exportuji...' : `💾 Export (${companies?.length || 0} firem)`}
          </button>
        </div>
      </div>

      {/* Bulk opravy dat - pouze pro admin */}
      {isAdmin && (
        <div className={`${isDarkMode ? 'bg-orange-900/20 border border-orange-600 border-l-4 border-l-orange-400' : 'bg-orange-50 border-l-4 border-orange-400'} p-4 rounded-lg`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-orange-200' : 'text-orange-800'}`}>⚠️ Bulk opravy dat (Admin)</h4>
          <div className="space-y-3">
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} p-3 rounded border`}>
              <h5 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🔧 Oprava forecast hodnot (všichni uživatelé)</h5>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Opraví nekonzistentní forecast hodnoty u všech firem v celém CRM systému:
              </p>
              <ul className={`text-xs mb-3 space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <li>• Všechny hodnoty s "%" → čísla bez "%" (např. "25%" → "25")</li>
                <li>• Duplicitní číselné hodnoty → string (např. 25 → "25")</li>
                <li>• "Forecast" → "0"</li>
                <li>• Unifikace všech forecast hodnot na stringy bez %</li>
                <li>• <strong>⚠️ Zpracovává firmy všech uživatelů v systému</strong></li>
              </ul>
              <div className="flex gap-2">
                <button
                  onClick={previewForecastIssues}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {isLoading ? '🔄 Kontroluji...' : '🔍 Zobrazit problémy (všichni)'}
                </button>
                <button
                  onClick={bulkFixForecastValues}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {isLoading ? '🔄 Opravuji...' : '🔧 Opravit všechny forecast hodnoty'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Automatické zálohování info */}
      <div className={`${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50'} p-4 rounded-lg`}>
        <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>💡 Tipy pro zálohování</h4>
        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <li>• <strong>JSON:</strong> Kompletní záloha včetně aktivit, novinek, zpráv a znalostí (admin)</li>
          <li>• <strong>Excel:</strong> Vhodné pro analýzu dat - firmy, uživatelé, aktivity, znalosti</li>
          <li>• <strong>CSV:</strong> Jednoduché tabulkové data firem pro další zpracování</li>
          <li>• <strong>Rozsah "Všechna data" obsahuje:</strong> Firmy, uživatele, novinky, zprávy, uživatelské znalosti, strukturu znalostí</li>
          <li>• <strong>Rozsah "Moje data" obsahuje:</strong> Pouze vlastní firmy a uživatelský profil</li>
          <li>• Doporučujeme pravidelné zálohování před velkými změnami</li>
          <li>• Zálohy ukládejte na bezpečné místo (cloud, externí disk)</li>
        </ul>
      </div>
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;
